/* eslint-disable no-nested-ternary */
/* eslint-disable camelcase */
import {
  useElement,
  useState,
  useStaleLayout,
  useRect,
  useEffect,
  useModel,
} from '@nebula.js/stardust';
import picassojs from 'picasso.js';
import picassoQ from 'picasso-plugin-q';
import picassoHammer from 'picasso-plugin-hammer';
import numeral from 'numeral';
import * as d3 from 'd3';
import lightOrDark from 'utils/lightordark';
import { easeExpOut as ease } from 'd3-ease';
import { timer } from 'd3-timer';
import { interpolate } from 'd3-interpolate';

import languagify from 'utils/languagify';

let transition = null;
const stopTransition = () => {
  if (transition) {
    transition.stop();
    transition = null;
  }
};

const findLogo = (name) => {
  switch (name) {
    case 'CBC News (CBC News Network News, CBC News Current Affairs, CBC News Local)':
      return 'CBC News, Current Affairs and Local';
    case 'Postmedia Network Incorporated':
      return 'Postmedia';
    case 'APTN':
      return 'APTN National News';
    case 'SaltWire':
      return 'Saltwire Network';
    case 'Radio-Canada':
      return 'radio-canada';
    default:
      return name;
  }
};

export default function supernova() {
  const picasso = picassojs();
  picasso.use(picassoQ);
  picasso.use(picassoHammer);

  picasso.component('swarm-imgs', {
    require: ['renderer', 'resolver'],
    render({ data }) {
      const { items } = this.resolver.resolve({
        data,
        settings: this.settings.settings,
      });

      const filteredItems = items.filter((item) => ['APTN', 'Toronto Star', 'The Tyee', 'APTN National News', 'CBC News (CBC News Network News, CBC News Current Affairs, CBC News Local)', 'La Presse', 'SaltWire', 'Postmedia Network Incorporated', 'Radio-Canada', 'Edmonton Journal', 'CBC News, Current Affairs and Local (CBC News (Network/National programming), CBC News Current Affairs, CBC News Local Services)', 'Postmedia', 'The Canadian Press', 'The Chronicle Herald', 'The Globe and Mail', 'The Hamilton Spectator', 'TVO', 'Winnipeg Free Press'].includes(item.data.value));
      const containerEl = this.renderer.element().parentNode;
      const oldImgEls = containerEl.querySelectorAll('img');
      for (let i = 0; i < oldImgEls.length; i++) {  //eslint-disable-line
        oldImgEls[i].remove();
      }
      for (let i = 0; i < filteredItems.length; i++) {  //eslint-disable-line

        const item = filteredItems[i];
        const size = item.size * Math.min(this.rect.width, this.rect.height) * 0.15;
        const scale = 0.55;
        const imgEl = document.createElement('img');
        imgEl.src = `./assets/logos/${findLogo(item.data.value)}.svg`;
        imgEl.style.opacity = '0.7';
        imgEl.width = size * scale;
        imgEl.style.position = 'absolute';
        imgEl.style.left = `${(item.x * this.rect.width) - scale * (size / 2)}px`;
        imgEl.style.top = `${(item.y * this.rect.height) - scale * (size / 2)}px`;
        containerEl.append(imgEl);
      }
      return [];
    },
  });

  picasso.component('swarm-legend', {
    require: ['renderer'],
    defaultSettings: {},
    render() {
      const components = [];

      const { genderToggled } = this.settings.settings;

      const fontFamily = 'Source Sans Pro';
      const fontSize = this.rect.width < 600 ? 9 : 11;
      const textSize = (text) => this.renderer.measureText({
        text,
        fontFamily,
        fontSize,
      });

      for (let i = 0; i < 1; i += 0.01) {
        components.push(
          {
            type: 'rect',
            x: this.rect.width * i,
            y: this.rect.height * 0.9,
            width: this.rect.width * (i + 0.1),
            height: 2,
            fill: d3.interpolateSpectral(i),
          },
        );
      }

      components.push(
        {
          type: 'line',
          stroke: d3.interpolateSpectral(0),
          strokeWidth: 2,
          x1: this.rect.width * 0 + 1,
          y1: this.rect.height * 0.9 + 2,
          x2: this.rect.width * 0 + 1,
          y2: this.rect.height * 0.9 + 2 + 4,
        },
      );
      components.push(
        {
          type: 'text',
          text: '100',
          fontFamily,
          fontSize,
          fill: d3.interpolateSpectral(0),
          x: this.rect.width * 0.001,
          y: this.rect.height * 0.9 + 6 + textSize('100').height,
        },
      );

      components.push(
        {
          type: 'line',
          stroke: d3.interpolateSpectral(0.125),
          strokeWidth: 2,
          x1: this.rect.width * 0.125,
          y1: this.rect.height * 0.9 + 2,
          x2: this.rect.width * 0.125,
          y2: this.rect.height * 0.9 + 2 + 4,
        },
      );

      components.push(
        {
          type: 'line',
          stroke: d3.interpolateSpectral(0.25),
          strokeWidth: 2,
          x1: this.rect.width * 0.25,
          y1: this.rect.height * 0.9 + 2,
          x2: this.rect.width * 0.25,
          y2: this.rect.height * 0.9 + 2 + 4,
        },
      );
      components.push(
        {
          type: 'text',
          text: '50',
          fontFamily,
          fontSize,
          fill: d3.interpolateSpectral(0.25),
          x: this.rect.width * 0.25 - textSize('50').width / 2,
          y: this.rect.height * 0.9 + 6 + textSize('50').height,
        },
      );

      components.push(
        {
          type: 'line',
          stroke: d3.interpolateSpectral(0.75),
          strokeWidth: 2,
          x1: this.rect.width * 0.75,
          y1: this.rect.height * 0.9 + 2,
          x2: this.rect.width * 0.75,
          y2: this.rect.height * 0.9 + 2 + 4,
        },
      );
      components.push(
        {
          type: 'text',
          text: '50',
          fontFamily,
          fontSize,
          fill: d3.interpolateSpectral(0.75),
          x: this.rect.width * 0.75 - textSize('50').width / 2,
          y: this.rect.height * 0.9 + 6 + textSize('50').height,
        },
      );

      components.push(
        {
          type: 'line',
          stroke: d3.interpolateSpectral(0.875),
          strokeWidth: 2,
          x1: this.rect.width * 0.875,
          y1: this.rect.height * 0.9 + 2,
          x2: this.rect.width * 0.875,
          y2: this.rect.height * 0.9 + 2 + 4,
        },
      );

      components.push(
        {
          type: 'line',
          stroke: d3.interpolateSpectral(1),
          strokeWidth: 2,
          x1: this.rect.width * 1 - 1,
          y1: this.rect.height * 0.9 + 2,
          x2: this.rect.width * 1 - 1,
          y2: this.rect.height * 0.9 + 2 + 4,
        },
      );
      components.push(
        {
          type: 'text',
          text: '100',
          fontFamily,
          fontSize,
          fill: d3.interpolateSpectral(1),
          x: this.rect.width * 0.9965 - textSize('100').width,
          y: this.rect.height * 0.9 + 6 + textSize('100').height,
        },
      );

      const textLines = [genderToggled ? languagify('More Men') : languagify('More White vs. Audience *  '), genderToggled ? languagify('More Women') : ((this.rect.width < 600) ? languagify('More Vis. Min. or Indig. vs. Audience *  ') : languagify('More Visible Minority or Indigenous vs. Audience *  '))].map((text) => ({
        text,
        fontFamily,
        fontSize,
        fill: '#000000',
        size: textSize(text),
      }));

      components.push(
        {
          type: 'line',
          stroke: '#5C5C5C',
          strokeWidth: 1,
          x1: this.rect.width * 0 + 4,
          y1: this.rect.height * 0.88 - textLines[0].size.height / 4,
          x2: this.rect.width * 1 - 4,
          y2: this.rect.height * 0.88 - textLines[0].size.height / 4,
        },
      );
      const triangle1X = this.rect.width * 0 + 4;
      const triangle2X = this.rect.width * 1 - 4;
      const triangleY = this.rect.height * 0.88 - textLines[0].size.height / 4;
      const triangleSize = 4;
      components.push(
        {
          type: 'path',
          d: `M ${triangle1X},${triangleY + triangleSize} ${triangle1X - triangleSize},${triangleY} ${triangle1X},${triangleY - triangleSize} z`,
          stroke: '#000000',
          fill: '#000000',
        },
      );
      components.push(
        {
          type: 'path',
          d: `M ${triangle2X},${triangleY + triangleSize} ${triangle2X + triangleSize},${triangleY} ${triangle2X},${triangleY - triangleSize} z`,
          stroke: '#000000',
          fill: '#000000',
        },
      );
      components.push(
        {
          type: 'rect',
          fill: '#FAFAF6',
          x: this.rect.width * 0.25 - textLines[0].size.width * (9 / 16),
          y: this.rect.height * 0.88 - textLines[0].size.height * (3 / 4),
          width: textLines[0].size.width + textLines[0].size.width * (1 / 8),
          height: textLines[0].size.height,
        },
      );
      components.push(
        {
          type: 'text',
          text: textLines[0].text,
          fontFamily: textLines[0].fontFamily,
          fontSize: textLines[0].fontSize,
          fill: textLines[0].fill,
          x: this.rect.width * 0.25 - textLines[0].size.width / 2,
          y: this.rect.height * 0.88,
        },
      );
      components.push(
        {
          type: 'rect',
          fill: '#FAFAF6',
          x: this.rect.width * 0.75 - textLines[1].size.width * (9 / 16),
          y: this.rect.height * 0.88 - textLines[1].size.height * (3 / 4),
          width: textLines[1].size.width + textLines[1].size.width * (1 / 8),
          height: textLines[1].size.height,
        },
      );
      components.push(
        {
          type: 'text',
          text: textLines[1].text,
          fontFamily: textLines[1].fontFamily,
          fontSize: textLines[1].fontSize,
          fill: textLines[1].fill,
          x: this.rect.width * 0.75 - textLines[1].size.width / 2,
          y: this.rect.height * 0.88,
        },
      );

      return components;
    },
  });

  picasso.component('swarm-avg', {
    require: ['renderer'],
    render({ data }) {
      const { genderToggled } = this.settings.settings;

      const items = data.items.map((item) => item.x.value);
      const avg = items.reduce((a, b) => a + b) / items.length;
      const scaledAvg = (avg / 200) + 0.5;

      const fontFamily = 'Source Sans Pro';
      const fontSize = 11;
      const textSize = (text) => this.renderer.measureText({
        text,
        fontFamily,
        fontSize,
      });

      const textLines = [languagify('Average'), `${+Math.abs(avg).toFixed(2)}${languagify('pts over-represented white')}`].map((text, index) => ({
        text,
        fontFamily,
        fontSize: index === '0' ? fontSize * 1.2 : fontSize,
        fill: '#36659C',
        size: textSize(text),
      }));

      return [
        {
          type: 'line',
          stroke: '#36659C',
          strokeWidth: 2,
          x1: this.rect.width * scaledAvg,
          y1: this.rect.height * 0.2,
          x2: this.rect.width * scaledAvg,
          y2: this.rect.height * 0.8,
        },
        {
          type: 'text',
          text: textLines[0].text,
          fontFamily: textLines[0].fontFamily,
          fontSize: textLines[0].fontSize,
          fill: textLines[0].fill,
          x: this.rect.width * scaledAvg - textLines[0].size.width / 2,
          y: genderToggled ? this.rect.height * 0.18 : this.rect.height * 0.11,
        },
        {
          type: 'text',
          text: genderToggled ? '' : textLines[1].text,
          fontFamily: textLines[1].fontFamily,
          fontSize: textLines[1].fontSize,
          fill: textLines[1].fill,
          x: this.rect.width * scaledAvg - textLines[1].size.width / 2,
          y: this.rect.height * 0.15,
        },
      ];
    },
  });

  picasso.component('swarm-parity', {
    require: ['renderer'],
    render() {
      const fontFamily = 'Source Sans Pro';
      const fontSize = 11;
      const textSize = (text) => this.renderer.measureText({
        text,
        fontFamily,
        fontSize,
      });

      const textLines = [languagify('Parity with'), languagify('Audience')].map((text) => ({
        text,
        fontFamily,
        fontSize,
        fill: '#000000',
        size: textSize(text),
      }));

      return [
        {
          type: 'line',
          stroke: '#5C5C5C',
          strokeWidth: 1,
          x1: this.rect.width * 0.5,
          y1: this.rect.height * 0.2,
          x2: this.rect.width * 0.5,
          y2: this.rect.height * 0.9,
        },
        {
          type: 'text',
          text: textLines[0].text,
          fontFamily: textLines[0].fontFamily,
          fontSize: textLines[0].fontSize,
          fill: textLines[0].fill,
          x: this.rect.width * 0.5 - textLines[0].size.width / 2,
          y: this.rect.height * 0.9 + textLines[0].size.height,
        },
        {
          type: 'text',
          text: textLines[1].text,
          fontFamily: textLines[1].fontFamily,
          fontSize: textLines[1].fontSize,
          fill: textLines[1].fill,
          x: this.rect.width * 0.5 - textLines[1].size.width / 2,
          y: this.rect.height * 0.9 + textLines[0].size.height + textLines[1].size.height,
        },
      ];
    },
  });

  return {
    qae: {
      properties: {
        qHyperCubeDef: {
          qDimensions: [],
          qMeasures: [],
          qInitialDataFetch: [{ qWidth: 7, qHeight: 1400 }],
          qSuppressZero: false,
          qSuppressMissing: true,
        },
        showTitles: true,
        title: '',
        subtitle: '',
        footnote: '',
      },
      data: {
        targets: [
          {
            path: '/qHyperCubeDef',
            dimensions: {
              min: 1,
              max: 3,
            },
            measures: {
              min: 1,
              max: 4,
            },
          },
        ],
      },
    },
    component() {
      const element = useElement();
      const layout = useStaleLayout();
      const rect = useRect();
      const model = useModel();

      const [instance, setInstance] = useState();
      const [data, setData] = useState([]);
      const [prevData, setPrevData] = useState([]);

      const genderToggled = !(layout.qHyperCube.qMeasureInfo[0].qFallbackTitle === '=Sum({<[Results - 2023]={"Disaggregate"}, [Name of media outlet for search - 2023]=>} [White Total - 2023]) / (Sum({<[Results - 2023]={"Disaggregate"}, [Name of media outlet for search - 2023]=>} [Total Employees - 2023]) - Sum({<[Results - 2023]={"Disaggregate"}, [Name of media outlet for search - 2023]=>} [Unknown Total - 2023]))');

      useEffect(() => {
        const p = picasso.chart({
        // const p = picasso({ renderer: { prio: ['canvas'] } }).chart({
          element,
          data: [],
          settings: {},
        });

        setInstance(p);

        return () => {
          p.destroy();
        };
      }, []);

      useEffect(() => {
        const layoutData = layout.qHyperCube.qDataPages[0].qMatrix;

        // Set Scales
        const radiusDomain = d3.extent(layoutData.map((row) => row[4].qNum));
        radiusDomain[0] = 1;
        const radiusScale = d3.scaleLog().domain(radiusDomain).range([0, 10]);  //eslint-disable-line

        const nodes = layoutData.map((row) => {
          const arr = [];
          arr.x = row[6].qNum * -1;
          arr.y = 0;
          arr.r = radiusScale(row[4].qNum);
          arr.data = {
            p: row[3].qNum,
            outlet: row[0].qText,
            outlet_pretty_name: row[1].qText,
            census_region: row[2].qText, // new
            perc_white_newsroom: row[3].qText,
            perc_white_census: row[5].qText,
            total_employees: row[4].qText,
            under_over_repres: row[6].qText * -1,
            elem_number: row[0].qElemNumber,
            perc_supervisors_white: row[0].qAttrExps.qValues[0].qText,
            perc_supervisors_black: row[0].qAttrExps.qValues[1].qText,
            perc_supervisors_asian: row[0].qAttrExps.qValues[2].qText,
            perc_supervisors_inuit: row[0].qAttrExps.qValues[3].qText,
            perc_supervisors_mixed: row[0].qAttrExps.qValues[4].qText,
            perc_supervisors_latin: row[0].qAttrExps.qValues[5].qText,
            perc_supervisors_meastern: row[0].qAttrExps.qValues[6].qText,
            perc_supervisors_unknown: row[0].qAttrExps.qValues[7].qText,
            perc_supervisors_women: row[0].qAttrExps.qValues[8].qText,
            perc_supervisors_men: row[0].qAttrExps.qValues[9].qText,
            perc_supervisors_nonbinary: row[0].qAttrExps.qValues[10].qText,
            perc_fulltime_white: row[0].qAttrExps.qValues[11].qText,
            perc_fulltime_black: row[0].qAttrExps.qValues[12].qText,
            perc_fulltime_asian: row[0].qAttrExps.qValues[13].qText,
            perc_fulltime_inuit: row[0].qAttrExps.qValues[14].qText,
            perc_fulltime_mixed: row[0].qAttrExps.qValues[15].qText,
            perc_fulltime_latin: row[0].qAttrExps.qValues[16].qText,
            perc_fulltime_meastern: row[0].qAttrExps.qValues[17].qText,
            perc_fulltime_unknown: row[0].qAttrExps.qValues[18].qText,
            perc_fulltime_women: row[0].qAttrExps.qValues[19].qText,
            perc_fulltime_men: row[0].qAttrExps.qValues[20].qText,
            perc_fulltime_nonbinary: row[0].qAttrExps.qValues[21].qText,
            perc_parttime_white: row[0].qAttrExps.qValues[22].qText,
            perc_parttime_black: row[0].qAttrExps.qValues[23].qText,
            perc_parttime_asian: row[0].qAttrExps.qValues[24].qText,
            perc_parttime_inuit: row[0].qAttrExps.qValues[25].qText,
            perc_parttime_mixed: row[0].qAttrExps.qValues[26].qText,
            perc_parttime_latin: row[0].qAttrExps.qValues[27].qText,
            perc_parttime_meastern: row[0].qAttrExps.qValues[28].qText,
            perc_parttime_unknown: row[0].qAttrExps.qValues[29].qText,
            perc_parttime_women: row[0].qAttrExps.qValues[30].qText,
            perc_parttime_men: row[0].qAttrExps.qValues[31].qText,
            perc_parttime_nonbinary: row[0].qAttrExps.qValues[32].qText,
            perc_intern_white: row[0].qAttrExps.qValues[33].qText,
            perc_intern_black: row[0].qAttrExps.qValues[34].qText,
            perc_intern_asian: row[0].qAttrExps.qValues[35].qText,
            perc_intern_inuit: row[0].qAttrExps.qValues[36].qText,
            perc_intern_mixed: row[0].qAttrExps.qValues[37].qText,
            perc_intern_latin: row[0].qAttrExps.qValues[38].qText,
            perc_intern_meastern: row[0].qAttrExps.qValues[39].qText,
            perc_intern_unknown: row[0].qAttrExps.qValues[40].qText,
            perc_intern_women: row[0].qAttrExps.qValues[41].qText,
            perc_intern_men: row[0].qAttrExps.qValues[42].qText,
            perc_intern_nonbinary: row[0].qAttrExps.qValues[43].qText,
            perc_allstaff_white: row[0].qAttrExps.qValues[44].qText,
            perc_allstaff_black: row[0].qAttrExps.qValues[45].qText,
            perc_allstaff_asian: row[0].qAttrExps.qValues[46].qText,
            perc_allstaff_inuit: row[0].qAttrExps.qValues[47].qText,
            perc_allstaff_mixed: row[0].qAttrExps.qValues[48].qText,
            perc_allstaff_latin: row[0].qAttrExps.qValues[49].qText,
            perc_allstaff_meastern: row[0].qAttrExps.qValues[50].qText,
            perc_allstaff_unknown: row[0].qAttrExps.qValues[51].qText,
            perc_allstaff_women: row[0].qAttrExps.qValues[52].qText,
            perc_allstaff_men: row[0].qAttrExps.qValues[53].qText,
            perc_allstaff_nonbinary: row[0].qAttrExps.qValues[54].qText,
            perc_census_white: row[0].qAttrExps.qValues[55].qText,
            perc_census_black: row[0].qAttrExps.qValues[56].qText,
            perc_census_asian: row[0].qAttrExps.qValues[57].qText,
            perc_census_inuit: row[0].qAttrExps.qValues[58].qText,
            perc_census_mixed: row[0].qAttrExps.qValues[59].qText,
            perc_census_latin: row[0].qAttrExps.qValues[60].qText,
            perc_census_meastern: row[0].qAttrExps.qValues[61].qText,
            perc_census_women: row[0].qAttrExps.qValues[62].qText,
            perc_census_men: row[0].qAttrExps.qValues[63].qText,
          };
          return arr;
        }).filter((node) => node.data.perc_white_census !== '-');

        const simulation = d3.forceSimulation(nodes)
          .force('x', d3.forceX().x((d) => d.x).strength(1))
          .force('y', d3.forceY().y(0).strength(0.2))
          .force('collide', d3.forceCollide((d) => d.r * 1.5).strength(1).iterations(5))
          .stop();

        while (simulation.alpha() > 0.01) {
          simulation.tick();
        }

        // Format data to pass to picasso
        const _data = [
          ['outlet',
            'outlet_pretty_name',
            'census_region',
            'perc_white_newsroom',
            'perc_white_census',
            'total_employees',
            'under_over_repres',
            'p',
            'x',
            'y',
            'r',
            'elem_number',
            'perc_supervisors_white',
            'perc_supervisors_black',
            'perc_supervisors_asian',
            'perc_supervisors_inuit',
            'perc_supervisors_mixed',
            'perc_supervisors_latin',
            'perc_supervisors_meastern',
            'perc_supervisors_unknown',
            'perc_supervisors_women',
            'perc_supervisors_men',
            'perc_supervisors_nonbinary',
            'perc_fulltime_white',
            'perc_fulltime_black',
            'perc_fulltime_asian',
            'perc_fulltime_inuit',
            'perc_fulltime_mixed',
            'perc_fulltime_latin',
            'perc_fulltime_meastern',
            'perc_fulltime_unknown',
            'perc_fulltime_women',
            'perc_fulltime_men',
            'perc_fulltime_nonbinary',
            'perc_parttime_white',
            'perc_parttime_black',
            'perc_parttime_asian',
            'perc_parttime_inuit',
            'perc_parttime_mixed',
            'perc_parttime_latin',
            'perc_parttime_meastern',
            'perc_parttime_unknown',
            'perc_parttime_women',
            'perc_parttime_men',
            'perc_parttime_nonbinary',
            'perc_intern_white',
            'perc_intern_black',
            'perc_intern_asian',
            'perc_intern_inuit',
            'perc_intern_mixed',
            'perc_intern_latin',
            'perc_intern_meastern',
            'perc_intern_unknown',
            'perc_intern_women',
            'perc_intern_men',
            'perc_intern_nonbinary',
            'perc_allstaff_white',
            'perc_allstaff_black',
            'perc_allstaff_asian',
            'perc_allstaff_inuit',
            'perc_allstaff_mixed',
            'perc_allstaff_latin',
            'perc_allstaff_meastern',
            'perc_allstaff_unknown',
            'perc_allstaff_women',
            'perc_allstaff_men',
            'perc_allstaff_nonbinary',
            'perc_census_white',
            'perc_census_black',
            'perc_census_asian',
            'perc_census_inuit',
            'perc_census_mixed',
            'perc_census_latin',
            'perc_census_meastern',
            'perc_census_women',
            'perc_census_men',
          ],
          ...nodes.map((node) => [
            node.data.outlet,
            node.data.outlet_pretty_name,
            node.data.census_region,
            node.data.perc_white_newsroom,
            node.data.perc_white_census,
            node.data.total_employees,
            node.data.under_over_repres,
            node.data.p,
            node.x,
            node.y,
            node.r,
            node.data.elem_number,
            node.data.perc_supervisors_white,
            node.data.perc_supervisors_black,
            node.data.perc_supervisors_asian,
            node.data.perc_supervisors_inuit,
            node.data.perc_supervisors_mixed,
            node.data.perc_supervisors_latin,
            node.data.perc_supervisors_meastern,
            node.data.perc_supervisors_unknown,
            node.data.perc_supervisors_women,
            node.data.perc_supervisors_men,
            node.data.perc_supervisors_nonbinary,
            node.data.perc_fulltime_white,
            node.data.perc_fulltime_black,
            node.data.perc_fulltime_asian,
            node.data.perc_fulltime_inuit,
            node.data.perc_fulltime_mixed,
            node.data.perc_fulltime_latin,
            node.data.perc_fulltime_meastern,
            node.data.perc_fulltime_unknown,
            node.data.perc_fulltime_women,
            node.data.perc_fulltime_men,
            node.data.perc_fulltime_nonbinary,
            node.data.perc_parttime_white,
            node.data.perc_parttime_black,
            node.data.perc_parttime_asian,
            node.data.perc_parttime_inuit,
            node.data.perc_parttime_mixed,
            node.data.perc_parttime_latin,
            node.data.perc_parttime_meastern,
            node.data.perc_parttime_unknown,
            node.data.perc_parttime_women,
            node.data.perc_parttime_men,
            node.data.perc_parttime_nonbinary,
            node.data.perc_intern_white,
            node.data.perc_intern_black,
            node.data.perc_intern_asian,
            node.data.perc_intern_inuit,
            node.data.perc_intern_mixed,
            node.data.perc_intern_latin,
            node.data.perc_intern_meastern,
            node.data.perc_intern_unknown,
            node.data.perc_intern_women,
            node.data.perc_intern_men,
            node.data.perc_intern_nonbinary,
            node.data.perc_allstaff_white,
            node.data.perc_allstaff_black,
            node.data.perc_allstaff_asian,
            node.data.perc_allstaff_inuit,
            node.data.perc_allstaff_mixed,
            node.data.perc_allstaff_latin,
            node.data.perc_allstaff_meastern,
            node.data.perc_allstaff_unknown,
            node.data.perc_allstaff_women,
            node.data.perc_allstaff_men,
            node.data.perc_allstaff_nonbinary,
            node.data.perc_census_white,
            node.data.perc_census_black,
            node.data.perc_census_asian,
            node.data.perc_census_inuit,
            node.data.perc_census_mixed,
            node.data.perc_census_latin,
            node.data.perc_census_meastern,
            node.data.perc_census_women,
            node.data.perc_census_men,
          ]),
        ];

        setData(_data);
      }, [layout]);

      useEffect(() => {
        if (!instance || !data.length) {
          return;
        }

        if (transition) {
          stopTransition();
        }

        const duration = 500;

        // Selected Outlet
        const selectedOutlet = layout.qHyperCube.qDataPages[0].qMatrix.filter((row) => row[0].qState === 'S').map((row) => row[0].qText);

        transition = timer((elapsed) => {
          const t = Math.min(1, ease(elapsed / duration));

          instance.update({
            data: [
              {
                type: 'matrix',
                data,
              },
            ],
            settings: {
              formatters: {
                percent: {
                  formatter: 'd3',
                  type: 'number',
                  format: ',.1%',
                },
              },
              scales: {
                x: {
                  data: {
                    field: 'x',
                  },
                  min: -104,
                  max: 104,
                },
                y: {
                  data: {
                    field: 'y',
                  },
                  min: -100,
                  max: 100,
                  invert: true,
                  ticks: {
                    values: [0],
                  },
                },
                r: {
                  data: {
                    field: 'r',
                  },
                  min: 0,
                  max: 10,
                },
                colorN: {
                  type: 'sequential-color',
                  domain: [-90, 0],
                  range: ['#B02C5F', '#FEF7AD'],
                },
                colorP: {
                  type: 'sequential-color',
                  domain: [0, 90],
                  range: ['#FEF7AD', '#5A55A5'],
                },
              },
              components: [
                {
                  type: 'grid-line',
                  y: {
                    scale: 'y',
                  },
                  ticks: {
                    show: true,
                    stroke: '#70707070',
                    strokeWidth: 1,
                    strokeDasharray: '3, 3',
                  },
                  minorTicks: {
                    show: false,
                  },
                },
                {
                  key: 'p',
                  type: 'point',
                  displayOrder: 1,
                  data: {
                    extract: {
                      field: 'outlet',
                      props: {
                        x: { field: 'x' },
                        y: { field: 'y' },
                        r: { field: 'r' },
                        outlet: { field: 'outlet' },
                        outlet_pretty_name: { field: 'outlet_pretty_name' },
                        census_region: { field: 'census_region' },
                        perc_white_census: { field: 'perc_white_census' },
                        perc_white_newsroom: { field: 'perc_white_newsroom' },
                        under_over_repres: { field: 'under_over_repres' },
                        total_employees: { field: 'total_employees' },
                        elem_number: { field: 'elem_number' },
                        perc_supervisors_white: { field: 'perc_supervisors_white' },
                        perc_supervisors_black: { field: 'perc_supervisors_black' },
                        perc_supervisors_asian: { field: 'perc_supervisors_asian' },
                        perc_supervisors_inuit: { field: 'perc_supervisors_inuit' },
                        perc_supervisors_mixed: { field: 'perc_supervisors_mixed' },
                        perc_supervisors_latin: { field: 'perc_supervisors_latin' },
                        perc_supervisors_meastern: { field: 'perc_supervisors_meastern' },
                        perc_supervisors_unknown: { field: 'perc_supervisors_unknown' },
                        perc_supervisors_women: { field: 'perc_supervisors_women' },
                        perc_supervisors_men: { field: 'perc_supervisors_men' },
                        perc_supervisors_nonbinary: { field: 'perc_supervisors_nonbinary' },
                        perc_fulltime_white: { field: 'perc_fulltime_white' },
                        perc_fulltime_black: { field: 'perc_fulltime_black' },
                        perc_fulltime_asian: { field: 'perc_fulltime_asian' },
                        perc_fulltime_inuit: { field: 'perc_fulltime_inuit' },
                        perc_fulltime_mixed: { field: 'perc_fulltime_mixed' },
                        perc_fulltime_latin: { field: 'perc_fulltime_latin' },
                        perc_fulltime_meastern: { field: 'perc_fulltime_meastern' },
                        perc_fulltime_unknown: { field: 'perc_fulltime_unknown' },
                        perc_fulltime_women: { field: 'perc_fulltime_women' },
                        perc_fulltime_men: { field: 'perc_fulltime_men' },
                        perc_fulltime_nonbinary: { field: 'perc_fulltime_nonbinary' },
                        perc_parttime_white: { field: 'perc_parttime_white' },
                        perc_parttime_black: { field: 'perc_parttime_black' },
                        perc_parttime_asian: { field: 'perc_parttime_asian' },
                        perc_parttime_inuit: { field: 'perc_parttime_inuit' },
                        perc_parttime_mixed: { field: 'perc_parttime_mixed' },
                        perc_parttime_latin: { field: 'perc_parttime_latin' },
                        perc_parttime_meastern: { field: 'perc_parttime_meastern' },
                        perc_parttime_unknown: { field: 'perc_parttime_unknown' },
                        perc_parttime_women: { field: 'perc_parttime_women' },
                        perc_parttime_men: { field: 'perc_parttime_men' },
                        perc_parttime_nonbinary: { field: 'perc_parttime_nonbinary' },
                        perc_intern_white: { field: 'perc_intern_white' },
                        perc_intern_black: { field: 'perc_intern_black' },
                        perc_intern_asian: { field: 'perc_intern_asian' },
                        perc_intern_inuit: { field: 'perc_intern_inuit' },
                        perc_intern_mixed: { field: 'perc_intern_mixed' },
                        perc_intern_latin: { field: 'perc_intern_latin' },
                        perc_intern_meastern: { field: 'perc_intern_meastern' },
                        perc_intern_unknown: { field: 'perc_intern_unknown' },
                        perc_intern_women: { field: 'perc_intern_women' },
                        perc_intern_men: { field: 'perc_intern_men' },
                        perc_intern_nonbinary: { field: 'perc_intern_nonbinary' },
                        perc_allstaff_white: { field: 'perc_allstaff_white' },
                        perc_allstaff_black: { field: 'perc_allstaff_black' },
                        perc_allstaff_asian: { field: 'perc_allstaff_asian' },
                        perc_allstaff_inuit: { field: 'perc_allstaff_inuit' },
                        perc_allstaff_mixed: { field: 'perc_allstaff_mixed' },
                        perc_allstaff_latin: { field: 'perc_allstaff_latin' },
                        perc_allstaff_meastern: { field: 'perc_allstaff_meastern' },
                        perc_allstaff_unknown: { field: 'perc_allstaff_unknown' },
                        perc_allstaff_women: { field: 'perc_allstaff_women' },
                        perc_allstaff_men: { field: 'perc_allstaff_men' },
                        perc_allstaff_nonbinary: { field: 'perc_allstaff_nonbinary' },
                        perc_census_white: { field: 'perc_census_white' },
                        perc_census_black: { field: 'perc_census_black' },
                        perc_census_asian: { field: 'perc_census_asian' },
                        perc_census_inuit: { field: 'perc_census_inuit' },
                        perc_census_mixed: { field: 'perc_census_mixed' },
                        perc_census_latin: { field: 'perc_census_latin' },
                        perc_census_meastern: { field: 'perc_census_meastern' },
                        perc_census_women: { field: 'perc_census_women' },
                        perc_census_men: { field: 'perc_census_men' },
                      },
                    },
                  },
                  settings: {
                    x: {
                      scale: 'x',
                      fn: (d) => {
                        const prev = prevData.find((p) => p[0] === d.datum.value);
                        if (prev) {
                          return interpolate(
                            d.scale(prev[8]),
                            d.scale(d.datum.x.value),
                          )(t);
                        }
                        return d.scale(d.datum.x.value);
                      },
                    },
                    y: {
                      scale: 'y',
                      fn: (d) => {
                        const prev = prevData.find((p) => p[0] === d.datum.value);
                        if (prev) {
                          return interpolate(
                            d.scale(prev[9]),
                            d.scale(d.datum.y.value),
                          )(t);
                        }
                        return d.scale(d.datum.y.value);
                      },
                    },
                    size: {
                      fn: (d) => {
                        const prev = prevData.find((p) => p[0] === d.datum.value);
                        if (prev) {
                          return interpolate(
                            d.resources.scale('r')(prev[10]),
                            d.resources.scale('r')(d.datum.r.value),
                          )(t);
                        }
                        return interpolate(0, d.resources.scale('r')(d.datum.r.value))(t);
                      },
                    },
                    sizeLimits: {
                      maxRelExtent: 0.15,
                    },
                    fill: {
                      fn: (d) => {
                        if (selectedOutlet.length) {
                          if (selectedOutlet.includes(d.datum.label)) {
                            return d3.interpolateSpectral(d.resources.scale('x')(d.datum.x.value));
                          }
                          return '#D3D3D3';
                        }
                        return d3.interpolateSpectral(d.resources.scale('x')(d.datum.x.value));
                      },
                    },
                    opacity: (d) => {
                      if (selectedOutlet.length) {
                        if (selectedOutlet.includes(d.datum.label)) {
                          return 1;
                        }
                        return '0.4';
                      }
                      return 1;
                    },
                    stroke: 'rgba(32, 32, 32, 0.25)',
                  },
                },
                {
                  type: 'swarm-imgs',
                  data: {
                    extract: {
                      field: 'outlet',
                      props: {
                        x: { field: 'x' },
                        y: { field: 'y' },
                        r: { field: 'r' },
                      },
                    },
                  },
                  settings: {
                    x: { scale: 'x', ref: 'x' },
                    y: { scale: 'y', ref: 'y' },
                    size: { scale: 'r', ref: 'r' },
                  },
                },
                {
                  type: 'swarm-legend',
                  settings: {
                    genderToggled,
                  },
                },
                {
                  type: 'swarm-avg',
                  displayOrder: 1,
                  data: {
                    extract: {
                      field: 'outlet',
                      props: {
                        x: { field: 'x' },
                      },
                    },
                  },
                  settings: {
                    genderToggled,
                  },
                },
                {
                  type: 'swarm-parity',
                  displayOrder: 0,
                },
                {
                  key: 'tooltip',
                  type: 'tooltip',
                  displayOrder: 10,
                  settings: {
                    duration: 20000,
                    // Target point marker
                    filter: (nodes) => nodes.filter((node) => node.type === 'circle'),
                    // Extract data
                    extract: ({ node }) => {
                      const obj = {};
                      obj.fill = node.attrs.fill;
                      obj.outlet_pretty_name = node.data.outlet_pretty_name.label;
                      obj.census_region = node.data.census_region.label;
                      obj.under_over_repres = node.data.under_over_repres.label;
                      // Supervisors
                      obj.perc_supervisors_white = node.data.perc_supervisors_white.label ? node.data.perc_supervisors_white.label : '';
                      obj.perc_supervisors_black = node.data.perc_supervisors_black.label ? node.data.perc_supervisors_black.label : '';
                      obj.perc_supervisors_asian = node.data.perc_supervisors_asian.label ? node.data.perc_supervisors_asian.label : '';
                      obj.perc_supervisors_inuit = node.data.perc_supervisors_inuit.label ? node.data.perc_supervisors_inuit.label : '';
                      obj.perc_supervisors_mixed = node.data.perc_supervisors_mixed.label ? node.data.perc_supervisors_mixed.label : '';
                      obj.perc_supervisors_meastern = node.data.perc_supervisors_meastern.label ? node.data.perc_supervisors_meastern.label : '';
                      obj.perc_supervisors_latin = node.data.perc_supervisors_latin.label ? node.data.perc_supervisors_latin.label : '';
                      obj.perc_supervisors_unknown = node.data.perc_supervisors_unknown.label ? node.data.perc_supervisors_unknown.label : '';
                      obj.perc_supervisors_women = node.data.perc_supervisors_women.label ? node.data.perc_supervisors_women.label : '';
                      obj.perc_supervisors_men = node.data.perc_supervisors_men.label ? node.data.perc_supervisors_men.label : '';
                      obj.perc_supervisors_nonbinary = node.data.perc_supervisors_nonbinary.label ? node.data.perc_supervisors_nonbinary.label : '';
                      // Fulltime
                      obj.perc_fulltime_white = node.data.perc_fulltime_white.label ? node.data.perc_fulltime_white.label : '';
                      obj.perc_fulltime_black = node.data.perc_fulltime_black.label ? node.data.perc_fulltime_black.label : '';
                      obj.perc_fulltime_asian = node.data.perc_fulltime_asian.label ? node.data.perc_fulltime_asian.label : '';
                      obj.perc_fulltime_inuit = node.data.perc_fulltime_inuit.label ? node.data.perc_fulltime_inuit.label : '';
                      obj.perc_fulltime_mixed = node.data.perc_fulltime_mixed.label ? node.data.perc_fulltime_mixed.label : '';
                      obj.perc_fulltime_meastern = node.data.perc_fulltime_meastern.label ? node.data.perc_fulltime_meastern.label : '';
                      obj.perc_fulltime_latin = node.data.perc_fulltime_latin.label ? node.data.perc_fulltime_latin.label : '';
                      obj.perc_fulltime_unknown = node.data.perc_fulltime_unknown.label ? node.data.perc_fulltime_unknown.label : '';
                      obj.perc_fulltime_women = node.data.perc_fulltime_women.label ? node.data.perc_fulltime_women.label : '';
                      obj.perc_fulltime_men = node.data.perc_fulltime_men.label ? node.data.perc_fulltime_men.label : '';
                      obj.perc_fulltime_nonbinary = node.data.perc_fulltime_nonbinary.label ? node.data.perc_fulltime_nonbinary.label : '';
                      // Parttime
                      obj.perc_parttime_white = node.data.perc_parttime_white.label ? node.data.perc_parttime_white.label : '';
                      obj.perc_parttime_black = node.data.perc_parttime_black.label ? node.data.perc_parttime_black.label : '';
                      obj.perc_parttime_asian = node.data.perc_parttime_asian.label ? node.data.perc_parttime_asian.label : '';
                      obj.perc_parttime_inuit = node.data.perc_parttime_inuit.label ? node.data.perc_parttime_inuit.label : '';
                      obj.perc_parttime_mixed = node.data.perc_parttime_mixed.label ? node.data.perc_parttime_mixed.label : '';
                      obj.perc_parttime_meastern = node.data.perc_parttime_meastern.label ? node.data.perc_parttime_meastern.label : '';
                      obj.perc_parttime_latin = node.data.perc_parttime_latin.label ? node.data.perc_parttime_latin.label : '';
                      obj.perc_parttime_unknown = node.data.perc_parttime_unknown.label ? node.data.perc_parttime_unknown.label : '';
                      obj.perc_parttime_women = node.data.perc_parttime_women.label ? node.data.perc_parttime_women.label : '';
                      obj.perc_parttime_men = node.data.perc_parttime_men.label ? node.data.perc_parttime_men.label : '';
                      obj.perc_parttime_nonbinary = node.data.perc_parttime_nonbinary.label ? node.data.perc_parttime_nonbinary.label : '';
                      // Interns
                      obj.perc_intern_white = node.data.perc_intern_white.label ? node.data.perc_intern_white.label : '';
                      obj.perc_intern_black = node.data.perc_intern_black.label ? node.data.perc_intern_black.label : '';
                      obj.perc_intern_asian = node.data.perc_intern_asian.label ? node.data.perc_intern_asian.label : '';
                      obj.perc_intern_inuit = node.data.perc_intern_inuit.label ? node.data.perc_intern_inuit.label : '';
                      obj.perc_intern_mixed = node.data.perc_intern_mixed.label ? node.data.perc_intern_mixed.label : '';
                      obj.perc_intern_meastern = node.data.perc_intern_meastern.label ? node.data.perc_intern_meastern.label : '';
                      obj.perc_intern_latin = node.data.perc_intern_latin.label ? node.data.perc_intern_latin.label : '';
                      obj.perc_intern_unknown = node.data.perc_intern_unknown.label ? node.data.perc_intern_unknown.label : '';
                      obj.perc_intern_women = node.data.perc_intern_women.label ? node.data.perc_intern_women.label : '';
                      obj.perc_intern_men = node.data.perc_intern_men.label ? node.data.perc_intern_men.label : '';
                      obj.perc_intern_nonbinary = node.data.perc_intern_nonbinary.label ? node.data.perc_intern_nonbinary.label : '';
                      // All Staff
                      obj.perc_allstaff_white = node.data.perc_allstaff_white.label ? node.data.perc_allstaff_white.label : '';
                      obj.perc_allstaff_black = node.data.perc_allstaff_black.label ? node.data.perc_allstaff_black.label : '';
                      obj.perc_allstaff_asian = node.data.perc_allstaff_asian.label ? node.data.perc_allstaff_asian.label : '';
                      obj.perc_allstaff_inuit = node.data.perc_allstaff_inuit.label ? node.data.perc_allstaff_inuit.label : '';
                      obj.perc_allstaff_mixed = node.data.perc_allstaff_mixed.label ? node.data.perc_allstaff_mixed.label : '';
                      obj.perc_allstaff_meastern = node.data.perc_allstaff_meastern.label ? node.data.perc_allstaff_meastern.label : '';
                      obj.perc_allstaff_latin = node.data.perc_allstaff_latin.label ? node.data.perc_allstaff_latin.label : '';
                      obj.perc_allstaff_unknown = node.data.perc_allstaff_unknown.label ? node.data.perc_allstaff_unknown.label : '';
                      obj.perc_allstaff_women = node.data.perc_allstaff_women.label ? node.data.perc_allstaff_women.label : '';
                      obj.perc_allstaff_men = node.data.perc_allstaff_men.label ? node.data.perc_allstaff_men.label : '';
                      obj.perc_allstaff_nonbinary = node.data.perc_allstaff_nonbinary.label ? node.data.perc_allstaff_nonbinary.label : '';
                      // Census
                      obj.perc_census_white = node.data.perc_census_white.label ? node.data.perc_census_white.label : '';
                      obj.perc_census_black = node.data.perc_census_black.label ? node.data.perc_census_black.label : '';
                      obj.perc_census_asian = node.data.perc_census_asian.label ? node.data.perc_census_asian.label : '';
                      obj.perc_census_inuit = node.data.perc_census_inuit.label ? node.data.perc_census_inuit.label : '';
                      obj.perc_census_mixed = node.data.perc_census_mixed.label ? node.data.perc_census_mixed.label : '';
                      obj.perc_census_meastern = node.data.perc_census_meastern.label ? node.data.perc_census_meastern.label : '';
                      obj.perc_census_latin = node.data.perc_census_latin.label ? node.data.perc_census_latin.label : '';
                      obj.perc_census_women = node.data.perc_census_women.label ? node.data.perc_census_women.label : '';
                      obj.perc_census_men = node.data.perc_census_men.label ? node.data.perc_census_men.label : '';
                      return obj;
                    },
                    content: ({ h, data }) => { // eslint-disable-line no-shadow
                      const els = [];

                      data.forEach((node) => {
                        const eltitle = h('div', {
                          style: {
                            backgroundColor: node.fill, padding: '5px', textAlign: 'center',
                          },
                        }, [h('span', {
                          style: {
                            color: lightOrDark(node.fill) === 'light' ? '#5C5C5C' : '#FFFFFF', textTransform: 'uppercase', fontWeight: 600, fontSize: 14,
                          },
                        }, node.outlet_pretty_name)]);

                        // headers
                        const elh = h('th', {}, '');
                        const elhWhite = h('th', { style: { padding: 4, fontWeight: 600 } }, languagify('White'));
                        const elhBlack = h('th', { style: { padding: 4, fontWeight: 600 } }, languagify('Black'));
                        const elhAsian = h('th', { style: { padding: 4, fontWeight: 600 } }, languagify('Asian'));
                        const elhInuit = h('th', { style: { padding: 4, fontWeight: 600 } }, languagify('Indigenous'));
                        const elhMixed = h('th', { style: { padding: 4, fontWeight: 600 } }, languagify('Mixed'));
                        const elhLatin = h('th', { style: { padding: 4, fontWeight: 600 } }, languagify('Latin'));
                        const elhMeast = h('th', { style: { padding: 4, fontWeight: 600 } }, languagify('M.Eas'));
                        const elhUnk = h('th', { style: { padding: 4, fontWeight: 600 } }, languagify('Unk.'));
                        const elhWomen = h('th', { style: { padding: 4, fontWeight: 600 } }, languagify('Women'));
                        const elhMen = h('th', { style: { padding: 4, fontWeight: 600 } }, languagify('Men'));
                        const elhNonbinary = h('th', { style: { padding: 4, fontWeight: 600 } }, languagify('Non-Bin'));

                        const el1_0 = h('td', { style: { padding: 4, fontWeight: 600 } }, languagify('Supervisors'));
                        const el1_1 = h('td', { style: { padding: 4 } }, (Number(node.perc_supervisors_white) === 0) ? '0' : ((Number(node.perc_supervisors_white) * 100 < 1) ? '<1%' : numeral(node.perc_supervisors_white).format('0.0%')));
                        const el1_2 = h('td', { style: { padding: 4 } }, (Number(node.perc_supervisors_black) === 0) ? '0' : ((Number(node.perc_supervisors_black) * 100 < 1) ? '<1%' : numeral(node.perc_supervisors_black).format('0.0%')));
                        const el1_3 = h('td', { style: { padding: 4 } }, (Number(node.perc_supervisors_asian) === 0) ? '0' : ((Number(node.perc_supervisors_asian) * 100 < 1) ? '<1%' : numeral(node.perc_supervisors_asian).format('0.0%')));
                        const el1_4 = h('td', { style: { padding: 4 } }, (Number(node.perc_supervisors_inuit) === 0) ? '0' : ((Number(node.perc_supervisors_inuit) * 100 < 1) ? '<1%' : numeral(node.perc_supervisors_inuit).format('0.0%')));
                        const el1_5 = h('td', { style: { padding: 4 } }, (Number(node.perc_supervisors_mixed) === 0) ? '0' : ((Number(node.perc_supervisors_mixed) * 100 < 1) ? '<1%' : numeral(node.perc_supervisors_mixed).format('0.0%')));
                        const el1_6 = h('td', { style: { padding: 4 } }, (Number(node.perc_supervisors_latin) === 0) ? '0' : ((Number(node.perc_supervisors_latin) * 100 < 1) ? '<1%' : numeral(node.perc_supervisors_latin).format('0.0%')));
                        const el1_7 = h('td', { style: { padding: 4 } }, (Number(node.perc_supervisors_meastern) === 0) ? '0' : ((Number(node.perc_supervisors_meastern) * 100 < 1) ? '<1%' : numeral(node.perc_supervisors_meastern).format('0.0%')));
                        const el1_8 = h('td', { style: { padding: 4 } }, (Number(node.perc_supervisors_unknown) === 0) ? '0' : ((Number(node.perc_supervisors_unknown) * 100 < 1) ? '<1%' : numeral(node.perc_supervisors_unknown).format('0.0%')));
                        const el1_9 = h('td', { style: { padding: 4 } }, (Number(node.perc_supervisors_women) === 0) ? '0' : ((Number(node.perc_supervisors_women) * 100 < 1) ? '<1%' : numeral(node.perc_supervisors_women).format('0.0%')));
                        const el1_10 = h('td', { style: { padding: 4 } }, (Number(node.perc_supervisors_men) === 0) ? '0' : ((Number(node.perc_supervisors_men) * 100 < 1) ? '<1%' : numeral(node.perc_supervisors_men).format('0.0%')));
                        const el1_11 = h('td', { style: { padding: 4 } }, (Number(node.perc_supervisors_nonbinary) === 0) ? '0' : ((Number(node.perc_supervisors_nonbinary) * 100 < 1) ? '<1%' : numeral(node.perc_supervisors_nonbinary).format('0.0%')));

                        const el2_0 = h('td', { style: { padding: 4, fontWeight: 600 } }, languagify('Full-Time'));
                        const el2_1 = h('td', { style: { padding: 4 } }, (Number(node.perc_fulltime_white) === 0) ? '0' : ((Number(node.perc_fulltime_white) * 100 < 1) ? '<1%' : numeral(node.perc_fulltime_white).format('0.0%')));
                        const el2_2 = h('td', { style: { padding: 4 } }, (Number(node.perc_fulltime_black) === 0) ? '0' : ((Number(node.perc_fulltime_black) * 100 < 1) ? '<1%' : numeral(node.perc_fulltime_black).format('0.0%')));
                        const el2_3 = h('td', { style: { padding: 4 } }, (Number(node.perc_fulltime_asian) === 0) ? '0' : ((Number(node.perc_fulltime_asian) * 100 < 1) ? '<1%' : numeral(node.perc_fulltime_asian).format('0.0%')));
                        const el2_4 = h('td', { style: { padding: 4 } }, (Number(node.perc_fulltime_inuit) === 0) ? '0' : ((Number(node.perc_fulltime_inuit) * 100 < 1) ? '<1%' : numeral(node.perc_fulltime_inuit).format('0.0%')));
                        const el2_5 = h('td', { style: { padding: 4 } }, (Number(node.perc_fulltime_mixed) === 0) ? '0' : ((Number(node.perc_fulltime_mixed) * 100 < 1) ? '<1%' : numeral(node.perc_fulltime_mixed).format('0.0%')));
                        const el2_6 = h('td', { style: { padding: 4 } }, (Number(node.perc_fulltime_latin) === 0) ? '0' : ((Number(node.perc_fulltime_latin) * 100 < 1) ? '<1%' : numeral(node.perc_fulltime_latin).format('0.0%')));
                        const el2_7 = h('td', { style: { padding: 4 } }, (Number(node.perc_fulltime_meastern) === 0) ? '0' : ((Number(node.perc_fulltime_meastern) * 100 < 1) ? '<1%' : numeral(node.perc_fulltime_meastern).format('0.0%')));
                        const el2_8 = h('td', { style: { padding: 4 } }, (Number(node.perc_fulltime_unknown) === 0) ? '0' : ((Number(node.perc_fulltime_unknown) * 100 < 1) ? '<1%' : numeral(node.perc_fulltime_unknown).format('0.0%')));
                        const el2_9 = h('td', { style: { padding: 4 } }, (Number(node.perc_fulltime_women) === 0) ? '0' : ((Number(node.perc_fulltime_women) * 100 < 1) ? '<1%' : numeral(node.perc_fulltime_women).format('0.0%')));
                        const el2_10 = h('td', { style: { padding: 4 } }, (Number(node.perc_fulltime_men) === 0) ? '0' : ((Number(node.perc_fulltime_men) * 100 < 1) ? '<1%' : numeral(node.perc_fulltime_men).format('0.0%')));
                        const el2_11 = h('td', { style: { padding: 4 } }, (Number(node.perc_fulltime_nonbinary) === 0) ? '0' : ((Number(node.perc_fulltime_nonbinary) * 100 < 1) ? '<1%' : numeral(node.perc_fulltime_nonbinary).format('0.0%')));

                        const el3_0 = h('td', { style: { padding: 4, fontWeight: 600 } }, languagify('Part-Time'));
                        const el3_1 = h('td', { style: { padding: 4 } }, (Number(node.perc_parttime_white) === 0) ? '0' : ((Number(node.perc_parttime_white) * 100 < 1) ? '<1%' : numeral(node.perc_parttime_white).format('0.0%')));
                        const el3_2 = h('td', { style: { padding: 4 } }, (Number(node.perc_parttime_black) === 0) ? '0' : ((Number(node.perc_parttime_black) * 100 < 1) ? '<1%' : numeral(node.perc_parttime_black).format('0.0%')));
                        const el3_3 = h('td', { style: { padding: 4 } }, (Number(node.perc_parttime_asian) === 0) ? '0' : ((Number(node.perc_parttime_asian) * 100 < 1) ? '<1%' : numeral(node.perc_parttime_asian).format('0.0%')));
                        const el3_4 = h('td', { style: { padding: 4 } }, (Number(node.perc_parttime_inuit) === 0) ? '0' : ((Number(node.perc_parttime_inuit) * 100 < 1) ? '<1%' : numeral(node.perc_parttime_inuit).format('0.0%')));
                        const el3_5 = h('td', { style: { padding: 4 } }, (Number(node.perc_parttime_mixed) === 0) ? '0' : ((Number(node.perc_parttime_mixed) * 100 < 1) ? '<1%' : numeral(node.perc_parttime_mixed).format('0.0%')));
                        const el3_6 = h('td', { style: { padding: 4 } }, (Number(node.perc_parttime_latin) === 0) ? '0' : ((Number(node.perc_parttime_latin) * 100 < 1) ? '<1%' : numeral(node.perc_parttime_latin).format('0.0%')));
                        const el3_7 = h('td', { style: { padding: 4 } }, (Number(node.perc_parttime_meastern) === 0) ? '0' : ((Number(node.perc_parttime_meastern) * 100 < 1) ? '<1%' : numeral(node.perc_parttime_meastern).format('0.0%')));
                        const el3_8 = h('td', { style: { padding: 4 } }, (Number(node.perc_parttime_unknown) === 0) ? '0' : ((Number(node.perc_parttime_unknown) * 100 < 1) ? '<1%' : numeral(node.perc_parttime_unknown).format('0.0%')));
                        const el3_9 = h('td', { style: { padding: 4 } }, (Number(node.perc_parttime_women) === 0) ? '0' : ((Number(node.perc_parttime_women) * 100 < 1) ? '<1%' : numeral(node.perc_parttime_women).format('0.0%')));
                        const el3_10 = h('td', { style: { padding: 4 } }, (Number(node.perc_parttime_men) === 0) ? '0' : ((Number(node.perc_parttime_men) * 100 < 1) ? '<1%' : numeral(node.perc_parttime_men).format('0.0%')));
                        const el3_11 = h('td', { style: { padding: 4 } }, (Number(node.perc_parttime_nonbinary) === 0) ? '0' : ((Number(node.perc_parttime_nonbinary) * 100 < 1) ? '<1%' : numeral(node.perc_parttime_nonbinary).format('0.0%')));

                        const el4_0 = h('td', { style: { padding: 4, fontWeight: 600 } }, languagify('Interns'));
                        const el4_1 = h('td', { style: { padding: 4 } }, (Number(node.perc_intern_white) === 0) ? '0' : ((Number(node.perc_intern_white) * 100 < 1) ? '<1%' : numeral(node.perc_intern_white).format('0.0%')));
                        const el4_2 = h('td', { style: { padding: 4 } }, (Number(node.perc_intern_black) === 0) ? '0' : ((Number(node.perc_intern_black) * 100 < 1) ? '<1%' : numeral(node.perc_intern_black).format('0.0%')));
                        const el4_3 = h('td', { style: { padding: 4 } }, (Number(node.perc_intern_asian) === 0) ? '0' : ((Number(node.perc_intern_asian) * 100 < 1) ? '<1%' : numeral(node.perc_intern_asian).format('0.0%')));
                        const el4_4 = h('td', { style: { padding: 4 } }, (Number(node.perc_intern_inuit) === 0) ? '0' : ((Number(node.perc_intern_inuit) * 100 < 1) ? '<1%' : numeral(node.perc_intern_inuit).format('0.0%')));
                        const el4_5 = h('td', { style: { padding: 4 } }, (Number(node.perc_intern_mixed) === 0) ? '0' : ((Number(node.perc_intern_mixed) * 100 < 1) ? '<1%' : numeral(node.perc_intern_mixed).format('0.0%')));
                        const el4_6 = h('td', { style: { padding: 4 } }, (Number(node.perc_intern_latin) === 0) ? '0' : ((Number(node.perc_intern_latin) * 100 < 1) ? '<1%' : numeral(node.perc_intern_latin).format('0.0%')));
                        const el4_7 = h('td', { style: { padding: 4 } }, (Number(node.perc_intern_meastern) === 0) ? '0' : ((Number(node.perc_intern_meastern) * 100 < 1) ? '<1%' : numeral(node.perc_intern_meastern).format('0.0%')));
                        const el4_8 = h('td', { style: { padding: 4 } }, (Number(node.perc_intern_unknown) === 0) ? '0' : ((Number(node.perc_intern_unknown) * 100 < 1) ? '<1%' : numeral(node.perc_intern_unknown).format('0.0%')));
                        const el4_9 = h('td', { style: { padding: 4 } }, (Number(node.perc_intern_women) === 0) ? '0' : ((Number(node.perc_intern_women) * 100 < 1) ? '<1%' : numeral(node.perc_intern_women).format('0.0%')));
                        const el4_10 = h('td', { style: { padding: 4 } }, (Number(node.perc_intern_men) === 0) ? '0' : ((Number(node.perc_intern_men) * 100 < 1) ? '<1%' : numeral(node.perc_intern_men).format('0.0%')));
                        const el4_11 = h('td', { style: { padding: 4 } }, (Number(node.perc_intern_nonbinary) === 0) ? '0' : ((Number(node.perc_intern_nonbinary) * 100 < 1) ? '<1%' : numeral(node.perc_intern_nonbinary).format('0.0%')));

                        const el5_0 = h('td', { style: { padding: 4, fontWeight: 600 } }, languagify('All Staff'));
                        const el5_1 = h('td', { style: { padding: 4 } }, (Number(node.perc_allstaff_white) === 0) ? '0' : ((Number(node.perc_allstaff_white) * 100 < 1) ? '<1%' : numeral(node.perc_allstaff_white).format('0.0%')));
                        const el5_2 = h('td', { style: { padding: 4 } }, (Number(node.perc_allstaff_black) === 0) ? '0' : ((Number(node.perc_allstaff_black) * 100 < 1) ? '<1%' : numeral(node.perc_allstaff_black).format('0.0%')));
                        const el5_3 = h('td', { style: { padding: 4 } }, (Number(node.perc_allstaff_asian) === 0) ? '0' : ((Number(node.perc_allstaff_asian) * 100 < 1) ? '<1%' : numeral(node.perc_allstaff_asian).format('0.0%')));
                        const el5_4 = h('td', { style: { padding: 4 } }, (Number(node.perc_allstaff_inuit) === 0) ? '0' : ((Number(node.perc_allstaff_inuit) * 100 < 1) ? '<1%' : numeral(node.perc_allstaff_inuit).format('0.0%')));
                        const el5_5 = h('td', { style: { padding: 4 } }, (Number(node.perc_allstaff_mixed) === 0) ? '0' : ((Number(node.perc_allstaff_mixed) * 100 < 1) ? '<1%' : numeral(node.perc_allstaff_mixed).format('0.0%')));
                        const el5_6 = h('td', { style: { padding: 4 } }, (Number(node.perc_allstaff_latin) === 0) ? '0' : ((Number(node.perc_allstaff_latin) * 100 < 1) ? '<1%' : numeral(node.perc_allstaff_latin).format('0.0%')));
                        const el5_7 = h('td', { style: { padding: 4 } }, (Number(node.perc_allstaff_meastern) === 0) ? '0' : ((Number(node.perc_allstaff_meastern) * 100 < 1) ? '<1%' : numeral(node.perc_allstaff_meastern).format('0.0%')));
                        const el5_8 = h('td', { style: { padding: 4 } }, (Number(node.perc_allstaff_unknown) === 0) ? '0' : ((Number(node.perc_allstaff_unknown) * 100 < 1) ? '<1%' : numeral(node.perc_allstaff_unknown).format('0.0%')));
                        const el5_9 = h('td', { style: { padding: 4 } }, (Number(node.perc_allstaff_women) === 0) ? '0' : ((Number(node.perc_allstaff_women) * 100 < 1) ? '<1%' : numeral(node.perc_allstaff_women).format('0.0%')));
                        const el5_10 = h('td', { style: { padding: 4 } }, (Number(node.perc_allstaff_men) === 0) ? '0' : ((Number(node.perc_allstaff_men) * 100 < 1) ? '<1%' : numeral(node.perc_allstaff_men).format('0.0%')));
                        const el5_11 = h('td', { style: { padding: 4 } }, (Number(node.perc_allstaff_nonbinary) === 0) ? '0' : ((Number(node.perc_allstaff_nonbinary) * 100 < 1) ? '<1%' : numeral(node.perc_allstaff_nonbinary).format('0.0%')));

                        const el6_0 = h('td', { style: { padding: 4, paddingBottom: 2, fontWeight: 600 } }, languagify('Census'));
                        const el6_1 = h('td', { style: { padding: 4 } }, (Number(node.perc_census_white) === 0) ? '0' : ((Number(node.perc_census_white) * 100 < 1) ? '<1%' : numeral(node.perc_census_white).format('0.0%')));
                        const el6_2 = h('td', { style: { padding: 4 } }, (Number(node.perc_census_black) === 0) ? '0' : ((Number(node.perc_census_black) * 100 < 1) ? '<1%' : numeral(node.perc_census_black).format('0.0%')));
                        const el6_3 = h('td', { style: { padding: 4 } }, (Number(node.perc_census_asian) === 0) ? '0' : ((Number(node.perc_census_asian) * 100 < 1) ? '<1%' : numeral(node.perc_census_asian).format('0.0%')));
                        const el6_4 = h('td', { style: { padding: 4 } }, (Number(node.perc_census_inuit) === 0) ? '0' : ((Number(node.perc_census_inuit) * 100 < 1) ? '<1%' : numeral(node.perc_census_inuit).format('0.0%')));
                        const el6_5 = h('td', { style: { padding: 4 } }, (Number(node.perc_census_mixed) === 0) ? '0' : ((Number(node.perc_census_mixed) * 100 < 1) ? '<1%' : numeral(node.perc_census_mixed).format('0.0%')));
                        const el6_6 = h('td', { style: { padding: 4 } }, (Number(node.perc_census_latin) === 0) ? '0' : ((Number(node.perc_census_latin) * 100 < 1) ? '<1%' : numeral(node.perc_census_latin).format('0.0%')));
                        const el6_7 = h('td', { style: { padding: 4 } }, (Number(node.perc_census_meastern) === 0) ? '0' : ((Number(node.perc_census_meastern) * 100 < 1) ? '<1%' : numeral(node.perc_census_meastern).format('0.0%')));
                        const el6_8 = h('td', { style: { padding: 4 } }, 'n/a');
                        const el6_9 = h('td', { style: { padding: 4 } }, (Number(node.perc_census_women) === 0) ? '0' : ((Number(node.perc_census_women) * 100 < 1) ? '<1%' : numeral(node.perc_census_women).format('0.0%')));
                        const el6_10 = h('td', { style: { padding: 4 } }, (Number(node.perc_census_men) === 0) ? '0' : ((Number(node.perc_census_men) * 100 < 1) ? '<1%' : numeral(node.perc_census_men).format('0.0%')));
                        const el6_11 = h('td', { style: { padding: 4 } }, 'n/a');

                        const el6_12 = h('td', {
                          style: {
                            paddingLeft: 4, paddingRight: 4, paddingBottom: 4, fontSize: 10,
                          },
                        }, `${node.census_region === 'National' ? 'Canada' : node.census_region}`);

                        const elrh = h('tr', {}, [elh, elhWhite, elhBlack, elhAsian, elhInuit, elhMixed, elhLatin, elhMeast, elhUnk, elhWomen, elhMen, elhNonbinary]);
                        const elr1 = h('tr', {}, [el1_0, el1_1, el1_2, el1_3, el1_4, el1_5, el1_6, el1_7, el1_8, el1_9, el1_10, el1_11]);
                        const elr2 = h('tr', {}, [el2_0, el2_1, el2_2, el2_3, el2_4, el2_5, el2_6, el2_7, el2_8, el2_9, el2_10, el2_11]);
                        const elr3 = h('tr', {}, [el3_0, el3_1, el3_2, el3_3, el3_4, el3_5, el3_6, el3_7, el3_8, el3_9, el3_10, el3_11]);
                        const elr4 = h('tr', {}, [el4_0, el4_1, el4_2, el4_3, el4_4, el4_5, el4_6, el4_7, el4_8, el4_9, el4_10, el4_11]);
                        const elr5 = h('tr', {}, [el5_0, el5_1, el5_2, el5_3, el5_4, el5_5, el5_6, el5_7, el5_8, el5_9, el5_10, el5_11]);
                        const elr6 = h('tr', {}, [el6_0, el6_1, el6_2, el6_3, el6_4, el6_5, el6_6, el6_7, el6_8, el6_9, el6_10, el6_11]);
                        const elr7 = h('tr', {}, [el6_12]);

                        els.push(eltitle, elrh, elr1, elr2, elr3, elr4, elr5, elr6, elr7);
                      });
                      return h('div', { style: { padding: 5 } }, els);
                    },
                    placement: {
                      type: 'pointer',
                      area: 'target',
                      dock: 'auto',
                    },
                  },
                },
              ],
              interactions: [
                {
                  type: 'native',
                  events: {
                    mousemove(e) {
                      this.chart.component('tooltip').emit('show', e);
                    },
                    mouseleave() {
                      this.chart.component('tooltip').emit('hide');
                    },
                  },
                },
                {
                  type: 'hammer',
                  gestures: [
                    {
                      type: 'Tap',
                      events: {
                        tap(e) {
                          const chartBounds = element.getBoundingClientRect();
                          const cx = e.center.x - chartBounds.left;
                          const cy = e.center.y - chartBounds.top;
                          const shapes = this.chart.shapesAt({ x: cx, y: cy });
                          const values = shapes.map((shape) => shape.data.elem_number.value);
                          model.selectHyperCubeValues('/qHyperCubeDef', 0, values, true);
                        },
                      },
                    },
                  ],
                },
              ],
            },
          });
          if (t === 1) {
            stopTransition();
            setPrevData(data);
          }
        });
      }, [data, instance]);

      useEffect(() => {
        if (!instance) {
          return;
        }
        instance.update();
      }, [rect.width, rect.height, instance]);
    },
  };
}
